import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Tooltip,
} from "@mui/material";
import { type FC, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useApiServiceCallback } from "../../../../server/api/client";
import type { UUID } from "../../../../utils/uuid";
import { useAnalytics } from "../../../analytics/analytics";
import { MomentService } from "../../../moment/client";

export const PublishToggleAction: FC<{
  id: UUID | null;
  status: "draft" | "paused" | "finished" | "active" | "archived" | null;
  reload: (active: boolean) => void;
}> = ({ id, status, reload }) => {
  const { t } = useTranslation();

  const [active, setActive] = useState(status === "active" || status === "finished");
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  const { gaEvent } = useAnalytics();

  const handleActiveStateChange = useApiServiceCallback(
    MomentService,
    async ({ apiService }): Promise<void> => {
      if (!id) return;
      setButtonLoading(true);
      const checked = !active;
      const response = await apiService.setActiveStatus(id, checked, true);
      if (!response.error) {
        setActive(checked);
        if (checked) {
          toast.success(t("Communication published"));
          gaEvent("publish", { event_category: "moment", moment_id: id, content_type: "card" });
        } else {
          toast.success(t("Communication unpublished"));
          gaEvent("unpublish", { event_category: "moment", moment_id: id, content_type: "card" });
        }
      } else {
        toast.error(
          checked
            ? t("Something went wrong when setting the Communication as active: {{error}}", {
                error: response.error,
              })
            : t("Something went wrong when pausing the Communication: {{error}}", {
                error: response.error,
              }),
        );
      }
      setButtonLoading(false);
      setOpenConfirmationDialog(false);
      reload(checked);
    },
    [active, id, t],
  );

  return (
    <>
      <Tooltip title={active ? t("Unpublish Communication") : t("Publish Communication")}>
        <Switch
          sx={{ mt: 0.3 }}
          data-testid="moment-active-toggle"
          checked={active}
          onClick={() => setOpenConfirmationDialog(true)}
          color="success"
        />
      </Tooltip>
      <Dialog
        open={openConfirmationDialog}
        onClose={() => setOpenConfirmationDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("Update Communication")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {active
              ? t("Are you sure you wish to pause this Communication?")
              : t("Are you sure you wish to make this Communication active?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="moment-card-publish-toggle-dialog-cancel"
            onClick={() => setOpenConfirmationDialog(false)}
          >
            {t("Cancel")}
          </Button>
          <Button
            data-analytics-id="moment-card-publish-toggle-dialog-confirm"
            type="submit"
            variant="outlined"
            loading={buttonLoading}
            onClick={() => {
              void handleActiveStateChange().catch(console.error);
            }}
          >
            {t("Yes, I'm sure")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
